/**
 * ChatPulseHome — the discovery + participation hub for chatspulse.pro.
 *
 * Sections (top → bottom):
 *   1. Hero — tagline + photo collage + handwritten accent
 *   2. Search + 3 primary CTAs (Start · Ask · Post About My Area)
 *   3. Explore by Place  (7 pill tabs, horizontally scrollable)
 *   4. Explore by Topic  (11+ pill tabs, horizontally scrollable)
 *   5. Trending Now      (3 image cards + "Join the Conversation" sidebar)
 *   6. Recent Activity   (list with thumbnails · location + tag chips + stats)
 *   7. Questions Needing Answers (Q&A list)
 *   8. Popular Communities (6 city cards)
 *
 * Empty-state strategy: while the DB is still small we surface a
 * carefully-curated fallback deck so the page still looks alive and
 * every card teaches new visitors what a great thread looks like.
 * The moment the backend returns real rows, those replace the fallbacks
 * 1-for-1.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, HelpCircle, MapPin, Compass, Hash, Globe, Flag,
  Building2, Home as HomeIcon, Navigation, Flame, MessageCircle,
  Eye, ThumbsUp, MoreHorizontal, CheckCircle2, Users, Newspaper,
  Plane, Utensils, Sprout, Briefcase, GraduationCap, Heart, Leaf,
  Bus, Palmtree, HelpingHand, Landmark, ArrowRight,
} from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/chatpulse/v1`;

// ─────────────────────────────────────────────────────────────
// Explore by Place — 7 tabs
// ─────────────────────────────────────────────────────────────
const PLACE_TABS = [
  { key: "near-me",           label: "Near Me",             Icon: Navigation },
  { key: "world",             label: "World",               Icon: Globe },
  { key: "countries",         label: "Countries",           Icon: Flag },
  { key: "states-provinces",  label: "States / Provinces",  Icon: Landmark },
  { key: "counties-districts",label: "Counties / Districts",Icon: Building2 },
  { key: "cities",            label: "Cities",              Icon: Building2 },
  { key: "towns-villages",    label: "Towns & Villages",    Icon: HomeIcon },
];

// ─────────────────────────────────────────────────────────────
// Explore by Topic — 11 visible + More overflow
// ─────────────────────────────────────────────────────────────
const TOPIC_TABS = [
  { key: "all",         label: "All",         Icon: Hash,           bg: "bg-blue-50",    fg: "text-blue-600" },
  { key: "local-news",  label: "Local News",  Icon: Newspaper,      bg: "bg-slate-100",  fg: "text-slate-700" },
  { key: "community",   label: "Community",   Icon: Users,          bg: "bg-emerald-50", fg: "text-emerald-600" },
  { key: "travel",      label: "Travel",      Icon: Plane,          bg: "bg-sky-50",     fg: "text-sky-600" },
  { key: "food",        label: "Food",        Icon: Utensils,       bg: "bg-orange-50",  fg: "text-orange-600" },
  { key: "farming",     label: "Farming",     Icon: Sprout,         bg: "bg-lime-50",    fg: "text-lime-700" },
  { key: "business",    label: "Business",    Icon: Briefcase,      bg: "bg-amber-50",   fg: "text-amber-700" },
  { key: "education",   label: "Education",   Icon: GraduationCap,  bg: "bg-indigo-50",  fg: "text-indigo-600" },
  { key: "health",      label: "Health",      Icon: Heart,          bg: "bg-rose-50",    fg: "text-rose-600" },
  { key: "nature",      label: "Nature",      Icon: Leaf,           bg: "bg-emerald-50", fg: "text-emerald-700" },
];
const MORE_TOPICS = [
  { key: "science", label: "Science" }, { key: "technology", label: "Technology" },
  { key: "sports", label: "Sports" },   { key: "movies", label: "Movies" },
  { key: "music", label: "Music" },     { key: "real-estate", label: "Real Estate" },
  { key: "jobs", label: "Jobs" },       { key: "events", label: "Events" },
  { key: "family-alumni", label: "Family & Alumni" }, { key: "transport", label: "Transport" },
];

// ─────────────────────────────────────────────────────────────
// Curated fallback content (only used until DB fills up)
// ─────────────────────────────────────────────────────────────
const TRENDING_FALLBACK = [
  {
    id: "seed-1",
    title: "Best time to see cherry blossoms in Japan?",
    place: { city: "Tokyo", country: "Japan" },
    tags: ["Travel", "Japan"],
    replies: 24, views: 1200, reactions: 56, age: "2h ago",
    image: "https://images.unsplash.com/photo-1522383225653-ed111181a951?w=800&auto=format&fit=crop&q=60",
  },
  {
    id: "seed-2",
    title: "Tips for starting a small farm in Oregon?",
    place: { city: "Oregon", country: "USA" },
    tags: ["Farming", "Small Business"],
    replies: 18, views: 842, reactions: 37, age: "4h ago",
    image: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=800&auto=format&fit=crop&q=60",
  },
  {
    id: "seed-3",
    title: "Where can I find good Kerala-style food in Portland?",
    place: { city: "Portland, Oregon", country: "USA" },
    tags: ["Food", "Local Recommendations"],
    replies: 32, views: 1100, reactions: 48, age: "6h ago",
    image: "https://images.unsplash.com/photo-1631292784640-2b24be784d5d?w=800&auto=format&fit=crop&q=60",
  },
];

const RECENT_FALLBACK = [
  { id: "r1", title: "Good hiking trails near Mount Hood?", place: "Oregon, USA",   tags: ["Travel", "Outdoors"],  replies: 12, views: 320, age: "1h ago", swatch: "from-sky-100 to-sky-200" },
  { id: "r2", title: "How to renew Indian passport in the USA?", place: "USA",          tags: ["Travel", "Documents", "Visa"], replies: 8, views: 290, age: "2h ago", swatch: "from-rose-100 to-rose-200" },
  { id: "r3", title: "Community events this weekend in Portland",place: "Portland, Oregon", tags: ["Events", "Community"], replies: 15, views: 410, age: "3h ago", swatch: "from-emerald-100 to-emerald-200" },
  { id: "r4", title: "Best crops for small farms in Kerala?",    place: "Kerala, India", tags: ["Farming", "Agriculture"], replies: 10, views: 260, age: "5h ago", swatch: "from-lime-100 to-lime-200" },
  { id: "r5", title: "Recommendations for family trip to Italy", place: "Italy",         tags: ["Travel", "Family"],      replies: 14, views: 380, age: "6h ago", swatch: "from-amber-100 to-amber-200" },
];

const QUESTIONS_FALLBACK = [
  { id: "q1", title: "Is there a good public transport option from Portland airport to downtown?", place: "Portland, Oregon, USA", answers: 3, Icon: Bus,       accent: "bg-amber-100 text-amber-700" },
  { id: "q2", title: "What vaccines are required for travel to Japan?",                              place: "USA",                    answers: 2, Icon: Plane,     accent: "bg-sky-100 text-sky-700" },
  { id: "q3", title: "Where can I donate surplus vegetables in Portland?",                            place: "Portland, Oregon, USA", answers: 1, Icon: HelpingHand,accent: "bg-emerald-100 text-emerald-700" },
  { id: "q4", title: "Any recommendations for good schools in Kottayam?",                             place: "Kottayam, Kerala, India",answers: 3, Icon: GraduationCap, accent: "bg-orange-100 text-orange-700" },
  { id: "q5", title: "What are the best budget hotels in Rome?",                                     place: "Rome, Italy",           answers: 2, Icon: Palmtree,  accent: "bg-indigo-100 text-indigo-700" },
];

const COMMUNITIES = [
  { slug: "portland-oregon-usa", city: "Portland", region: "Oregon, USA", discussions: 128, image: "https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=600&auto=format&fit=crop&q=60" },
  { slug: "kerala-india",        city: "Kerala",   region: "India",       discussions: 342, image: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=600&auto=format&fit=crop&q=60" },
  { slug: "tokyo-japan",         city: "Tokyo",    region: "Japan",       discussions: 298, image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=600&auto=format&fit=crop&q=60" },
  { slug: "london-uk",           city: "London",   region: "United Kingdom", discussions: 215, image: "https://images.unsplash.com/photo-1533929736458-ca588d08c8be?w=600&auto=format&fit=crop&q=60" },
  { slug: "new-york-usa",        city: "New York", region: "USA",         discussions: 310, image: "https://images.unsplash.com/photo-1496588152823-86ff7695e68f?w=600&auto=format&fit=crop&q=60" },
  { slug: "sydney-australia",    city: "Sydney",   region: "Australia",   discussions: 188, image: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=600&auto=format&fit=crop&q=60" },
];

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
export default function ChatPulseHome() {
  const [query, setQuery]         = useState("");
  const [placeTab, setPlaceTab]   = useState("world");
  const [topicTab, setTopicTab]   = useState("all");
  const [hot, setHot]             = useState(null);
  const [recent, setRecent]       = useState(null);
  const [questions, setQuestions] = useState(null);
  const [communities, setCommunities] = useState(null);
  const [myFollows, setMyFollows] = useState(null);

  // Rebuild filter query-string whenever the tabs change.
  const filterQs = useMemo(() => {
    const p = new URLSearchParams();
    if (topicTab && topicTab !== "all") p.set("category", topicTab);
    return p.toString();
  }, [topicTab]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [h, r, q, c] = await Promise.all([
          fetch(`${BASE}/hot?limit=3${filterQs ? `&${filterQs}` : ""}`).then((x) => x.json()),
          fetch(`${BASE}/timeline?limit=6${filterQs ? `&${filterQs}` : ""}`).then((x) => x.json()),
          fetch(`${BASE}/questions-needing-answers?limit=5${filterQs ? `&${filterQs}` : ""}`).then((x) => x.json()),
          fetch(`${BASE}/communities/popular?limit=6`).then((x) => x.json()),
        ]);
        if (cancel) return;
        setHot(h.threads || []);
        setRecent(r.threads || []);
        setQuestions(q.threads || []);
        setCommunities(c.communities || []);
      } catch {
        if (cancel) return;
        setHot([]); setRecent([]); setQuestions([]); setCommunities([]);
      }
    })();
    return () => { cancel = true; };
  }, [filterQs]);

  // Fetch follows — silently 401s for signed-out users.
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/follows/me`,
          { credentials: "include" })
      .then((r) => r.ok ? r.json() : { follows: [] })
      .then((d) => setMyFollows(d.follows || []))
      .catch(() => setMyFollows([]));
  }, []);

  const trendingCards = useMemo(() => {
    if (hot && hot.length) return hot.slice(0, 3).map(threadToTrendingCard);
    return TRENDING_FALLBACK;
  }, [hot]);

  const recentCards = useMemo(() => {
    if (recent && recent.length) return recent.slice(0, 5).map(threadToRecentCard);
    return RECENT_FALLBACK;
  }, [recent]);

  const questionCards = useMemo(() => {
    if (questions && questions.length) return questions.slice(0, 5).map(threadToQuestionCard);
    return QUESTIONS_FALLBACK;
  }, [questions]);

  const communityCards = useMemo(() => {
    if (communities && communities.length) return communities.slice(0, 6).map(rowToCommunityCard);
    return COMMUNITIES;
  }, [communities]);

  return (
    <div className="bg-white text-slate-900" data-testid="chatpulse-home">
      <Hero query={query} setQuery={setQuery} />

      <div className="max-w-6xl mx-auto px-4 lg:px-6">
        {myFollows && myFollows.length > 0 && <MyCommunitiesStrip follows={myFollows} />}
        <ExploreByPlace active={placeTab} onSelect={setPlaceTab} />
        <ExploreByTopic active={topicTab} onSelect={setTopicTab} />

        <section className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8" data-testid="chatpulse-trending">
          <TrendingSection cards={trendingCards} filterLabel={topicTab !== "all" ? prettyCat(topicTab) : null} />
          <JoinTheConversation />
        </section>

        <section className="mt-14 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
          <RecentActivity cards={recentCards} />
          <QuestionsNeedingAnswers items={questionCards} />
        </section>

        <PopularCommunities items={communityCards} />
      </div>

      <BottomStrip />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────
function Hero({ query, setQuery }) {
  return (
    <section
      className="relative overflow-hidden bg-gradient-to-b from-sky-50/60 via-white to-white"
      data-testid="chatpulse-hero"
    >
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-10 lg:py-14 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-8 items-start">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-slate-900">
            Every community.<br />Every topic.<br />
            <span className="text-slate-900">One conversation.</span>
          </h1>
          <p className="mt-5 text-base text-slate-600 max-w-lg">
            Discover conversations from your neighborhood and around the world.
            Ask questions, exchange knowledge, and connect through the SocialPulse network.
          </p>

          {/* Search */}
          <div className="mt-6 relative max-w-xl" data-testid="chatpulse-search">
            <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search places, topics, questions or discussions…"
              className="w-full pl-11 pr-4 py-3 text-sm border border-slate-200 rounded-full outline-none focus:border-blue-400 bg-white shadow-sm"
              data-testid="chatpulse-search-input"
              aria-label="Search discussions"
            />
          </div>

          {/* 3 primary CTAs */}
          <div className="mt-5 flex flex-wrap gap-2.5" data-testid="chatpulse-primary-ctas">
            <CtaButton to="/thread/new" testId="cta-start-discussion" Icon={MessageCircle} tone="solid">Start a Discussion</CtaButton>
            <CtaButton to="/thread/new?kind=question" testId="cta-ask-question" Icon={HelpCircle} tone="outline">Ask a Question</CtaButton>
            <CtaButton to="/thread/new?scope=near" testId="cta-post-area" Icon={MapPin} tone="outline">Post About My Area</CtaButton>
          </div>
        </div>

        {/* Photo collage + handwritten accent */}
        <div className="hidden lg:block relative min-h-[280px]">
          <div className="absolute inset-0">
            <div className="absolute right-[46%] top-2 w-40 h-40 rotate-[-8deg] rounded-md overflow-hidden shadow-xl border-4 border-white">
              <img src="https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=400&auto=format&fit=crop&q=60" alt="Local" className="w-full h-full object-cover" loading="lazy" />
              <span className="absolute top-1.5 left-2 text-[10px] font-black italic text-slate-700 tracking-tight">Local</span>
            </div>
            <div className="absolute right-[22%] top-6 w-44 h-44 rotate-[3deg] rounded-md overflow-hidden shadow-xl border-4 border-white">
              <img src="https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=400&auto=format&fit=crop&q=60" alt="National" className="w-full h-full object-cover" loading="lazy" />
              <span className="absolute top-1.5 left-2 text-[10px] font-black italic text-slate-700 tracking-tight">National</span>
            </div>
            <div className="absolute right-0 top-12 w-40 h-40 rotate-[-4deg] rounded-md overflow-hidden shadow-xl border-4 border-white">
              <img src="https://images.unsplash.com/photo-1522383225653-ed111181a951?w=400&auto=format&fit=crop&q=60" alt="Global" className="w-full h-full object-cover" loading="lazy" />
              <span className="absolute top-1.5 left-2 text-[10px] font-black italic text-slate-700 tracking-tight">Global</span>
            </div>

            <div className="absolute right-8 bottom-2 text-right text-sm text-slate-600 font-[cursive] leading-tight">
              <div className="italic">Real people.</div>
              <div className="italic">Real places.</div>
              <div className="italic border-b-2 border-blue-400 inline-block pb-0.5">Real conversations.</div>
              <div className="text-[10px] text-slate-400 mt-1">A more connected world starts with you.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaButton({ to, testId, Icon, tone, children }) {
  const base = "inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors";
  const solid = "bg-blue-600 text-white hover:bg-blue-700 shadow-sm";
  const outline = "bg-white text-slate-900 border border-slate-200 hover:border-blue-400 hover:text-blue-700";
  return (
    <Link to={to} data-testid={testId} className={`${base} ${tone === "solid" ? solid : outline}`}>
      <Icon className="w-4 h-4" /> {children}
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────
// Explore-by-place tabs
// ─────────────────────────────────────────────────────────────
function ExploreByPlace({ active, onSelect }) {
  return (
    <section className="mt-10" data-testid="explore-by-place">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="inline-flex items-center gap-2 font-bold text-slate-900">
          <Globe className="w-4 h-4 text-blue-600" /> Explore by Place
        </h2>
        <Link to="/places" className="text-xs font-semibold text-slate-500 hover:text-blue-700" data-testid="explore-all-locations">
          View all locations <ArrowRight className="inline w-3 h-3" />
        </Link>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
        {PLACE_TABS.map(({ key, label, Icon }) => (
          <Link
            key={key}
            to={key === "near-me" ? "/places/near-me" : "/places"}
            data-testid={`place-tab-${key}`}
            onClick={() => onSelect(key)}
            className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${
              active === key
                ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </Link>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Explore-by-topic tabs
// ─────────────────────────────────────────────────────────────
function ExploreByTopic({ active, onSelect }) {
  const [showMore, setShowMore] = useState(false);
  return (
    <section className="mt-6" data-testid="explore-by-topic">
      <h2 className="inline-flex items-center gap-2 font-bold text-slate-900 mb-3">
        <Hash className="w-4 h-4 text-blue-600" /> Explore by Topic
      </h2>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
        {TOPIC_TABS.map(({ key, label, Icon, bg, fg }) => {
          const isActive = active === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(key)}
              data-testid={`topic-tab-${key}`}
              className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold border transition-colors ${
                isActive
                  ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                  : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
              }`}
            >
              <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center ${isActive ? "bg-white/20" : bg}`}>
                <Icon className={`w-3 h-3 ${isActive ? "text-white" : fg}`} />
              </span>
              {label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          data-testid="topic-tab-more"
          className="shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold border bg-white text-slate-700 border-slate-200 hover:border-blue-400"
        >
          <MoreHorizontal className="w-3.5 h-3.5" /> More
        </button>
      </div>
      {showMore && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="topic-more-list">
          {MORE_TOPICS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => { onSelect(key); setShowMore(false); }}
              className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-300 text-slate-700"
              data-testid={`topic-more-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Trending Now
// ─────────────────────────────────────────────────────────────
function TrendingSection({ cards, filterLabel }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="inline-flex items-center gap-2 font-bold text-slate-900 text-lg">
            <Flame className="w-5 h-5 text-orange-500" /> Trending Now
            {filterLabel && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-100" data-testid="trending-filter-label">
                {filterLabel}
              </span>
            )}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Popular discussions from around the world</p>
        </div>
        <Link to="/trending" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="trending-view-all">
          View all <ArrowRight className="inline w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trending-cards">
        {cards.map((c) => (
          <TrendingCard key={c.id} card={c} />
        ))}
      </div>
    </div>
  );
}

function TrendingCard({ card }) {
  return (
    <Link
      to={`/thread/${card.id}`}
      className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg hover:border-blue-300 transition"
      data-testid={`trending-card-${card.id}`}
    >
      <div className="aspect-[16/10] overflow-hidden bg-slate-100">
        <img src={card.image} alt={card.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-3.5">
        <h3 className="font-bold text-sm text-slate-900 leading-snug line-clamp-2">{card.title}</h3>
        <div className="mt-1.5 text-xs text-slate-500 inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {card.place.city}{card.place.country ? `, ${card.place.country}` : ""}
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {card.tags.map((t) => <TagPill key={t}>{t}</TagPill>)}
        </div>
        <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {card.replies}</span>
          <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {formatCount(card.views)}</span>
          <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {card.reactions}</span>
          <span className="ml-auto">{card.age}</span>
        </div>
      </div>
    </Link>
  );
}

function TagPill({ children }) {
  return (
    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Join the Conversation sidebar
// ─────────────────────────────────────────────────────────────
function JoinTheConversation() {
  const rows = [
    "Follow the places and topics you love",
    "Get local insights and real advice",
    "Connect with people worldwide",
    "Be part of a positive global community",
  ];
  return (
    <aside
      className="rounded-2xl bg-blue-50 border border-blue-100 p-5 h-fit"
      data-testid="join-the-conversation"
    >
      <h3 className="inline-flex items-center gap-2 font-bold text-blue-800">
        <Users className="w-4 h-4" /> Join the Conversation
      </h3>
      <p className="text-xs text-blue-800/80 mt-1.5">
        Share your knowledge, ask questions, help others, and make your community stronger.
      </p>
      <ul className="mt-3 space-y-2">
        {rows.map((r) => (
          <li key={r} className="text-xs text-blue-900 inline-flex items-start gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" /> {r}
          </li>
        ))}
      </ul>
      <Link
        to="/signin"
        className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
        data-testid="join-signin-btn"
      >
        Sign in to join
      </Link>
      <p className="text-[10px] text-blue-800/60 mt-2 text-center">Read freely. Join in when you're signed in.</p>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────
// Recent Activity
// ─────────────────────────────────────────────────────────────
function RecentActivity({ cards }) {
  return (
    <div data-testid="recent-activity">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="inline-flex items-center gap-2 font-bold text-slate-900 text-lg">
            <MessageCircle className="w-5 h-5 text-blue-600" /> Recent Activity
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Latest discussions across the SocialPulse network</p>
        </div>
        <Link to="/timeline" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="recent-view-all">
          View all <ArrowRight className="inline w-3 h-3" />
        </Link>
      </div>
      <ul className="space-y-2">
        {cards.map((c) => <RecentRow key={c.id} card={c} />)}
      </ul>
    </div>
  );
}

function RecentRow({ card }) {
  return (
    <Link
      to={`/thread/${card.id}`}
      className="group flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm transition"
      data-testid={`recent-row-${card.id}`}
    >
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${card.swatch || "from-slate-100 to-slate-200"} shrink-0 flex items-center justify-center`}>
        <MapPin className="w-4 h-4 text-slate-500/70" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-sm text-slate-900 leading-snug line-clamp-1 group-hover:text-blue-700">{card.title}</h3>
        <div className="mt-1 text-xs text-slate-500 inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {card.place}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {card.tags.map((t) => <TagPill key={t}>{t}</TagPill>)}
        </div>
      </div>
      <div className="text-right shrink-0 text-[11px] text-slate-500 hidden sm:block min-w-[80px]">
        <div className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {card.replies}</div>
        <div className="inline-flex items-center gap-1 ml-2"><Eye className="w-3 h-3" /> {formatCount(card.views)}</div>
        <div className="text-slate-400 mt-1">{card.age}</div>
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────
// Questions Needing Answers
// ─────────────────────────────────────────────────────────────
function QuestionsNeedingAnswers({ items }) {
  return (
    <div data-testid="questions-needing-answers">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="inline-flex items-center gap-2 font-bold text-slate-900 text-lg">
            <HelpCircle className="w-5 h-5 text-red-500" /> Questions Needing Answers
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Help someone today</p>
        </div>
        <Link to="/questions" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="questions-view-all">
          View all <ArrowRight className="inline w-3 h-3" />
        </Link>
      </div>
      <ul className="space-y-2">
        {items.map((q) => (
          <Link
            key={q.id}
            to={`/thread/${q.id}`}
            className="group flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm transition"
            data-testid={`question-${q.id}`}
          >
            <span className={`w-9 h-9 rounded-lg shrink-0 inline-flex items-center justify-center ${q.accent}`}>
              <q.Icon className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-blue-700">{q.title}</p>
              <div className="mt-1 text-xs text-slate-500 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {q.place}
              </div>
            </div>
            <div className="text-right text-[11px] text-slate-500 shrink-0 inline-flex items-center gap-1">
              <MessageCircle className="w-3 h-3" /> {q.answers} {q.answers === 1 ? "answer" : "answers"}
            </div>
          </Link>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Popular Communities
// ─────────────────────────────────────────────────────────────
function PopularCommunities({ items }) {
  return (
    <section className="mt-14 mb-10" data-testid="popular-communities">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="inline-flex items-center gap-2 font-bold text-slate-900 text-lg">
          <Users className="w-5 h-5 text-blue-600" /> Popular Communities
        </h2>
        <Link to="/communities" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="communities-view-all">
          Explore more <ArrowRight className="inline w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {items.map((c) => (
          <Link
            key={c.slug}
            to={`/place/${c.slug}`}
            className="group rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg hover:border-blue-300 transition"
            data-testid={`community-${c.slug}`}
          >
            <div className="aspect-[4/3] overflow-hidden bg-slate-100">
              <img src={c.image} alt={c.city} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-2.5">
              <div className="font-bold text-sm text-slate-900">{c.city}</div>
              <div className="text-[11px] text-slate-500">{c.region}</div>
              <div className="text-[11px] text-blue-600 mt-1">{c.discussions} discussions</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Bottom strip (four pillar links + handwritten accent)
// ─────────────────────────────────────────────────────────────
function BottomStrip() {
  const links = [
    { to: "/people",        label: "People",        Icon: Users },
    { to: "/places",        label: "Places",        Icon: MapPin },
    { to: "/conversations", label: "Conversations", Icon: MessageCircle },
    { to: "/communities",   label: "Communities",   Icon: Compass },
  ];
  return (
    <section className="border-t border-slate-200 mt-4 py-6" data-testid="chatpulse-bottom-strip">
      <div className="max-w-6xl mx-auto px-4 lg:px-6 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex flex-wrap gap-6">
          {links.map(({ to, label, Icon }) => (
            <Link
              key={label}
              to={to}
              data-testid={`bottom-strip-${label.toLowerCase()}`}
              className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-blue-700"
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </Link>
          ))}
        </div>
        <div className="text-sm text-slate-500 font-[cursive] italic">
          Different places. Shared stories. <span className="border-b-2 border-blue-400 pb-0.5">A better tomorrow.</span>
        </div>
      </div>
    </section>
  );
}

function MyCommunitiesStrip({ follows }) {
  return (
    <section className="mt-8" data-testid="my-communities-strip">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="inline-flex items-center gap-2 font-bold text-slate-900">
          <Users className="w-4 h-4 text-blue-600" /> My Communities
          <span className="text-[11px] font-normal text-slate-400">· {follows.length} following</span>
        </h2>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
        {follows.map((f) => {
          const to = f.type === "place" ? `/places/${f.slug}` : `/c/${f.slug}`;
          return (
            <Link
              key={`${f.type}-${f.slug}`}
              to={to}
              data-testid={`my-community-${f.type}-${f.slug}`}
              className="shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold border border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-400 hover:bg-blue-100"
            >
              {f.type === "place" ? <MapPin className="w-3.5 h-3.5" /> : <Hash className="w-3.5 h-3.5" />}
              {f.name}
              <span className="text-[10px] font-normal opacity-70">{f.discussions_count}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function formatCount(n) {
  if (n == null) return "0";
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
}

function threadToQuestionCard(t, i) {
  const fb = QUESTIONS_FALLBACK[i % QUESTIONS_FALLBACK.length];
  const place = t.location
    ? [t.location.city, t.location.state, t.location.country].filter(Boolean).join(", ")
    : (t.context_label || "Global");
  return {
    id: t.id,
    title: t.title,
    place,
    answers: t.post_count || 0,
    Icon: fb.Icon,
    accent: fb.accent,
  };
}

function rowToCommunityCard(row, i) {
  const fb = COMMUNITIES[i % COMMUNITIES.length];
  return {
    slug: row.slug || fb.slug,
    city: row.city || fb.city,
    region: row.region || fb.region,
    discussions: row.discussions,
    image: fb.image,
  };
}

function threadToTrendingCard(t, i) {
  const fallback = TRENDING_FALLBACK[i] || TRENDING_FALLBACK[0];
  return {
    id: t.id,
    title: t.title,
    place: t.location
      ? { city: t.location.city || t.location.state || "", country: t.location.country || "" }
      : { city: t.context_label || "Global", country: "" },
    tags: t.tags || (t.category ? [prettyCat(t.category)] : ["Community"]),
    replies: t.post_count || 0,
    views: t.view_count || 0,
    reactions: t.reaction_count || 0,
    age: timeAgo(t.last_post_at || t.created_at),
    image: t.cover_image_url || fallback.image,
  };
}

function threadToRecentCard(t, i) {
  const fb = RECENT_FALLBACK[i] || RECENT_FALLBACK[0];
  return {
    id: t.id,
    title: t.title,
    place: t.location
      ? [t.location.city, t.location.state, t.location.country].filter(Boolean).join(", ")
      : (t.context_label || "Global"),
    tags: t.tags || (t.category ? [prettyCat(t.category)] : ["Community"]),
    replies: t.post_count || 0,
    views: t.view_count || 0,
    age: timeAgo(t.last_post_at || t.created_at),
    swatch: fb.swatch,
  };
}

function prettyCat(c) {
  if (!c) return "Community";
  return c.split("-").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" ");
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
